import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Heart, Shield, Star, Clock, ArrowRight, Smartphone, Video, Baby, Stethoscope, Calendar, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LandingPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Modern Design */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" fill="currentColor" />
              <span>{t('trustedMaternalChildHealthcare')}</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
              {t('healthcareThatBridgesGap')}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
              {t('professionalTelemedicine')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/signup"
                className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
              >
                {t('getStartedFree')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl border-2 border-gray-200 font-semibold text-lg hover:border-emerald-600 hover:text-emerald-600 transition-all duration-300"
              >
                {t('signIn')}
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto pt-8 border-t border-gray-200">
              <div>
                <div className="text-3xl font-bold text-gray-900">10,000+</div>
                <div className="text-sm text-gray-600">{t('patientsServed')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">30+</div>
                <div className="text-sm text-gray-600">{t('healthcareProviders')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">4.9★</div>
                <div className="text-sm text-gray-600">{t('averageRating')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {t('everythingYouNeed')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('comprehensiveMaternal')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50 hover:shadow-xl transition-all duration-300 border border-green-100">
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Video className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('videoConsultations')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('videoConsultationsDesc')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-xl transition-all duration-300 border border-purple-100">
              <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Heart className="w-7 h-7 text-white" fill="currentColor" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('maternalCare')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('maternalCareDesc')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 hover:shadow-xl transition-all duration-300 border border-blue-100">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Baby className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('childHealthCare')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('childHealthDesc')}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 hover:shadow-xl transition-all duration-300 border border-orange-100">
              <div className="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('availability24_7')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('availability24_7Desc')}
              </p>
            </div>

            {/* Feature 5 */}
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 hover:shadow-xl transition-all duration-300 border border-emerald-100">
              <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('healthTrackingService')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('healthTrackingDesc')}
              </p>
            </div>

            {/* Feature 6 */}
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-rose-50 to-red-50 hover:shadow-xl transition-all duration-300 border border-rose-100">
              <div className="w-14 h-14 bg-rose-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('securePrivate')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('securePrivateDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {t('howItWorks')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('getStartedInSteps')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Connection Line */}
              <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-600 to-emerald-600"></div>

              {/* Step 1 */}
              <div className="relative flex flex-col lg:flex-row items-center mb-16 lg:mb-24">
                <div className="lg:w-1/2 lg:pr-12 mb-8 lg:mb-0">
                  <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-green-100">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4">
                      1
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('createYourAccount')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('createAccountDesc')}
                    </p>
                  </div>
                </div>
                <div className="lg:w-1/2 lg:pl-12 flex justify-center">
                  <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center shadow-xl">
                    <Smartphone className="w-10 h-10 text-white" />
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative flex flex-col lg:flex-row-reverse items-center mb-16 lg:mb-24">
                <div className="lg:w-1/2 lg:pl-12 mb-8 lg:mb-0">
                  <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-emerald-100">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4">
                      2
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('bookAppointment')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('bookAppointmentDesc')}
                    </p>
                  </div>
                </div>
                <div className="lg:w-1/2 lg:pr-12 flex justify-center">
                  <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center shadow-xl">
                    <Calendar className="w-10 h-10 text-white" />
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative flex flex-col lg:flex-row items-center">
                <div className="lg:w-1/2 lg:pr-12 mb-8 lg:mb-0">
                  <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-emerald-100">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-green-600 rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4">
                      3
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('getQualityCare')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      {t('getQualityCareDesc')}
                    </p>
                  </div>
                </div>
                <div className="lg:w-1/2 lg:pl-12 flex justify-center">
                  <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center shadow-xl">
                    <Stethoscope className="w-10 h-10 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {t('whatPatientsSay')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('realStoriesFromRealPeople')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-100">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('testimonial1')}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                  A
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('testimonial1Name')}</div>
                  <div className="text-sm text-gray-600">{t('testimonial1Title')}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl border border-purple-100">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('testimonial2')}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                  C
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('testimonial2Name')}</div>
                  <div className="text-sm text-gray-600">{t('testimonial2Title')}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-8 rounded-2xl border border-blue-100">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('testimonial3')}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  Y
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('testimonial3Name')}</div>
                  <div className="text-sm text-gray-600">{t('testimonial3Title')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-emerald-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('readyToGetStarted')}
          </h2>
          <p className="text-xl text-green-50 mb-10 max-w-2xl mx-auto">
            {t('joinThousands')}
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-white text-green-600 px-10 py-5 rounded-xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            {t('createFreeAccount')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
